<template>
  <main class="simple-root" :class="{ 'is-rtl': isRTL }">
    <section class="simple-shell">
      <header class="simple-header">
        <div class="simple-brand">
          <span>{{ t('nav.brand') }}</span>
          <h1>{{ simpleCopy.title }}</h1>
          <p>{{ simpleCopy.subtitle }}</p>
        </div>
        <select class="locale-switcher" :value="locale" @change="switchLocale($event.target.value)">
          <option v-for="option in localeOptions" :key="option.code" :value="option.code">{{ option.name }}</option>
        </select>
      </header>

      <section class="simple-action-panel">
        <button class="simple-start-button" type="button" :disabled="loading || processing" @click="startSimulation">
          {{ buttonLabel }}
        </button>
        <p v-if="summaryText" class="simple-summary">
          {{ summaryText }}
          <button class="simple-ranking-link" type="button" @click="openChampionRanking">
            {{ rankingCopy.trigger }}
          </button>
        </p>
        <p v-if="run?.completed" class="simple-support">
          {{ supportCopy.before }}
          <a href="/wc26/" target="_blank" rel="noopener noreferrer">{{ supportCopy.advanced }}</a>
          {{ supportCopy.middle }}
          <a :href="AUTHOR_LINKS.x" target="_blank" rel="noopener noreferrer">{{ supportCopy.x }}</a>
          {{ supportCopy.separator }}
          <a :href="AUTHOR_LINKS.xiaohongshu" target="_blank" rel="noopener noreferrer">{{ supportCopy.xiaohongshu }}</a>
          {{ supportCopy.after }}
        </p>
        <p v-if="errorMessage" class="error simple-error" role="alert">{{ errorMessage }}</p>
      </section>

      <section class="simple-fixtures-panel">
        <div class="section-heading compact simple-fixtures-heading">
          <div>
            <h2>{{ t('nav.fixtures') }}</h2>
            <p>{{ fixtureProgress }}</p>
          </div>
        </div>

        <div v-if="loading" class="simple-loading">{{ t('common.processing') }}</div>
        <div v-else class="simple-fixture-list">
          <article v-for="group in groupedFixtures" :key="group.key" class="simple-date-group">
            <h3>{{ group.label }}</h3>
            <div class="simple-match-grid">
              <div
                v-for="item in group.items"
                :key="item.fixture.id"
                class="simple-match-card"
                :class="{ completed: item.match, pending: !item.match }"
              >
                <div class="simple-match-meta">
                  <span>{{ stageLabel(item.fixture) }}</span>
                  <em>{{ item.fixture.venue || '' }}</em>
                </div>
                <div class="simple-match-row">
                  <strong><span v-html="teamFlag(item.fixture.homeTeamId)"></span> {{ teamName(item.fixture.homeTeamId) }}</strong>
                  <span class="simple-score" v-html="scoreHtml(item)"></span>
                  <strong>{{ teamName(item.fixture.awayTeamId) }} <span v-html="teamFlag(item.fixture.awayTeamId)"></span></strong>
                </div>
                <div v-if="goalEvents(item, item.fixture.homeTeamId).length || goalEvents(item, item.fixture.awayTeamId).length" class="fixture-scorers-row simple-scorers-row">
                  <div class="fixture-scorers home-side">
                    <span v-for="goal in goalEvents(item, item.fixture.homeTeamId)" :key="goal.key">{{ goal.minute }}' {{ goal.player }}</span>
                  </div>
                  <div></div>
                  <div class="fixture-scorers away-side">
                    <span v-for="goal in goalEvents(item, item.fixture.awayTeamId)" :key="goal.key">{{ goal.minute }}' {{ goal.player }}</span>
                  </div>
                </div>
                <p v-if="item.match?.penaltyShootout" class="simple-decision">
                  {{ t('match.shootout') }} {{ item.match.penaltyShootout.score.home }}-{{ item.match.penaltyShootout.score.away }}
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>
    </section>

    <div v-if="simulationOverlayOpen" class="simple-sim-overlay" role="status" aria-live="polite">
      <p class="simple-support simple-sim-support">
        {{ supportCopy.before }}
        <a href="/wc26/" target="_blank" rel="noopener noreferrer">{{ supportCopy.advanced }}</a>
        {{ supportCopy.middle }}
        <a :href="AUTHOR_LINKS.x" target="_blank" rel="noopener noreferrer">{{ supportCopy.x }}</a>
        {{ supportCopy.separator }}
        <a :href="AUTHOR_LINKS.xiaohongshu" target="_blank" rel="noopener noreferrer">{{ supportCopy.xiaohongshu }}</a>
        {{ supportCopy.after }}
      </p>
      <div class="simple-sim-modal">
        <div class="simple-sim-head">
          <div>
            <span>{{ simulationOverlayTitle }}</span>
            <h2>{{ fixtureProgress }}</h2>
          </div>
        </div>
        <div ref="simulationLogEl" class="simple-sim-results">
          <div v-if="simulationResults.length === 0" class="simple-sim-empty">{{ simpleCopy.simulating }}</div>
          <article v-for="result in simulationResults" :key="result.id" class="simple-sim-result">
            <span>{{ result.date }}</span>
            <em>{{ result.stage }}</em>
            <strong>{{ result.home }} {{ result.score }} {{ result.away }}</strong>
          </article>
        </div>
      </div>
    </div>

    <div v-if="championRankingOpen" class="simple-ranking-overlay" role="dialog" aria-modal="true">
      <section class="simple-ranking-modal">
        <header class="simple-ranking-head">
          <div>
            <span>{{ t('nav.brand') }}</span>
            <h2>{{ rankingCopy.title }}</h2>
          </div>
          <button type="button" class="simple-ranking-close" :aria-label="t('common.close')" @click="championRankingOpen = false">
            {{ t('common.close') }}
          </button>
        </header>
        <div class="simple-ranking-list">
          <div v-if="rankingsLoading" class="mini-empty">{{ t('common.processing') }}</div>
          <div v-else-if="championRankingRows.length === 0" class="mini-empty">{{ t('landing.noChampion') }}</div>
          <template v-else>
            <div v-for="(row, index) in championRankingRows" :key="row.team.id" class="mini-row simple-ranking-row">
              <span>{{ index + 1 }}. <span v-html="teamFlag(row.team.id)"></span> {{ teamName(row.team.id) }}</span>
              <strong>{{ t('landing.championCount', { count: row.count }) }}</strong>
            </div>
          </template>
        </div>
      </section>
    </div>
  </main>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from '../i18n/index.js';
import { loadWorldCupData, createInitialRun, stepRun } from '../engine/tournament.js';
import { KNOCKOUT_FIXTURE_SLOTS } from '../data/constants.js';
import { flag, newRunSeed, shortDate, sleep, stageName, translatedTeamName, strongestTeam } from '../utils/helpers.js';
import { finalScore, finalScoreHtml, matchTeamGoalEvents } from '../utils/match.js';
import { allKnownFixtures, fixtureDateKey, fixtureSortValue } from '../utils/fixture.js';

const { locale, isRTL, t, switchLocale } = useI18n();

const localeOptions = [
  { code: 'zh-CN', name: '简体中文' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
  { code: 'ar', name: 'العربية' },
  { code: 'ja', name: '日本語' },
];

const manifest = ref(null);
const run = ref(null);
const loading = ref(true);
const processing = ref(false);
const errorMessage = ref('');
const simulationOverlayOpen = ref(false);
const simulationResults = ref([]);
const simulationLogEl = ref(null);
const rankingsData = ref(null);
const rankingsLoading = ref(false);
const championRankingOpen = ref(false);
const championRecorded = ref(false);

const AUTHOR_LINKS = {
  x: 'https://x.com/diysimon',
  xiaohongshu: 'https://www.xiaohongshu.com/user/profile/5d33efc90000000011000840',
};

const simpleCopy = computed(() => {
  const copy = {
    'zh-CN': { title: '2026 世界杯', subtitle: '一键模拟全部赛程。', simulating: '正在模拟中', completed: '模拟已完成' },
    en: { title: 'World Cup 2026', subtitle: 'Simulate every score in one click.', simulating: 'Simulating', completed: 'Simulation complete' },
    fr: { title: 'Coupe du monde 2026', subtitle: 'Simulez tous les scores en un clic.', simulating: 'Simulation en cours', completed: 'Simulation terminée' },
    es: { title: 'Mundial 2026', subtitle: 'Simula todos los marcadores con un clic.', simulating: 'Simulando', completed: 'Simulación completada' },
    de: { title: 'WM 2026', subtitle: 'Alle Ergebnisse mit einem Klick simulieren.', simulating: 'Simulation läuft', completed: 'Simulation beendet' },
    pt: { title: 'Copa 2026', subtitle: 'Simule todos os placares com um clique.', simulating: 'Simulando', completed: 'Simulação concluída' },
    ar: { title: 'كأس العالم 2026', subtitle: 'حاك كل النتائج بنقرة واحدة.', simulating: 'جار المحاكاة', completed: 'اكتملت المحاكاة' },
    ja: { title: '2026 ワールドカップ', subtitle: '全試合のスコアをワンクリックでシミュレート。', simulating: 'シミュレーション中', completed: 'シミュレーション完了' },
  };
  return copy[locale.value] || copy.en;
});

const supportCopy = computed(() => {
  const copy = {
    'zh-CN': {
      before: '点击',
      advanced: '这里',
      middle: '即可体验更高级的世界杯模拟游戏，欢迎关注作者自媒体账号（',
      x: 'X',
      separator: ' / ',
      xiaohongshu: '小红书',
      after: '），是对作者最大的支持。',
    },
    en: {
      before: 'Click ',
      advanced: 'here',
      middle: ' to try the advanced World Cup simulation game. Following the author on ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' is the best way to support the project.',
    },
    fr: {
      before: 'Cliquez ',
      advanced: 'ici',
      middle: ' pour essayer le simulateur Coupe du monde avancé. Suivre l’auteur sur ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' est le meilleur soutien.',
    },
    es: {
      before: 'Haz clic ',
      advanced: 'aquí',
      middle: ' para probar el simulador avanzado del Mundial. Seguir al autor en ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' es el mejor apoyo.',
    },
    de: {
      before: 'Klicke ',
      advanced: 'hier',
      middle: ', um die erweiterte WM-Simulation zu spielen. Folge dem Autor auf ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' - das ist die beste Unterstützung.',
    },
    pt: {
      before: 'Clique ',
      advanced: 'aqui',
      middle: ' para experimentar o simulador avançado da Copa. Seguir o autor em ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' é o maior apoio.',
    },
    ar: {
      before: 'اضغط ',
      advanced: 'هنا',
      middle: ' لتجربة لعبة محاكاة كأس العالم المتقدمة. متابعة الكاتب على ',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: ' هي أفضل دعم.',
    },
    ja: {
      before: '',
      advanced: 'ここ',
      middle: 'をクリックすると、より高度なワールドカップシミュレーションを体験できます。作者を',
      x: 'X',
      separator: ' / ',
      xiaohongshu: 'Xiaohongshu',
      after: 'でフォローしていただけると最大の応援になります。',
    },
  };
  return copy[locale.value] || copy.en;
});

const rankingCopy = computed(() => {
  const copy = {
    'zh-CN': { trigger: '模拟夺冠排行榜', title: '模拟夺冠排行榜' },
    en: { trigger: 'Simulated Champion Ranking', title: 'Simulated Champion Ranking' },
    fr: { trigger: 'Classement des champions simulés', title: 'Classement des champions simulés' },
    es: { trigger: 'Ranking de campeones simulados', title: 'Ranking de campeones simulados' },
    de: { trigger: 'Simulierte Champion-Rangliste', title: 'Simulierte Champion-Rangliste' },
    pt: { trigger: 'Ranking de campeões simulados', title: 'Ranking de campeões simulados' },
    ar: { trigger: 'ترتيب الأبطال في المحاكاة', title: 'ترتيب الأبطال في المحاكاة' },
    ja: { trigger: 'シミュレーション優勝ランキング', title: 'シミュレーション優勝ランキング' },
  };
  return copy[locale.value] || copy.en;
});

const simulationOverlayTitle = computed(() => (processing.value ? simpleCopy.value.simulating : simpleCopy.value.completed));

const buttonLabel = computed(() => {
  if (loading.value || processing.value) return t('common.processing');
  if (run.value?.completed) return t('common.playAgain');
  return t('mobile.landing.start');
});

const playedCount = computed(() => run.value?.matches?.length || 0);
const totalFixtureCount = computed(() => Math.max(104, fixtureItems.value.length));
const fixtureProgress = computed(() => `${playedCount.value}/${totalFixtureCount.value}`);

const championRankingRows = computed(() => {
  if (!rankingsData.value?.champion) return [];
  return rankingsData.value.champion
    .map((item) => ({ team: teamById(item.teamId), count: item.count }))
    .filter((row) => row.team);
});

const summaryText = computed(() => {
  if (!run.value?.completed) return '';
  const final = run.value.matches.find((match) => match.stage === 'Final');
  const winner = final?.winnerTeamId || final?.penaltyWinnerTeamId;
  if (!winner) return t('common.completed');
  const name = teamName(winner);
  const templates = {
    'zh-CN': `已完成，${name} 获得了冠军`,
    en: `Completed, ${name} won the championship`,
    fr: `Terminé, ${name} remporte le titre`,
    es: `Completado, ${name} ganó el campeonato`,
    de: `Beendet, ${name} ist Champion`,
    pt: `Concluído, ${name} conquistou o título`,
    ar: `اكتمل، ${name} فاز بالبطولة`,
    ja: `完了、${name} が優勝しました`,
  };
  return templates[locale.value] || templates.en;
});

const fixtureItems = computed(() => {
  if (!manifest.value) return [];
  const matchesById = new Map((run.value?.matches || []).map((match) => [match.id, match]));
  const source = run.value
    ? allKnownFixtures(run.value, manifest.value.fixtures)
    : [
        ...manifest.value.fixtures,
        ...KNOCKOUT_FIXTURE_SLOTS.map((slot) => ({
          id: `match-${slot.id}`,
          stage: slot.stage,
          group: null,
          date: slot.date,
          venue: slot.venue,
          homeTeamId: 'tbd',
          awayTeamId: 'tbd',
          pending: true,
        })),
      ];
  return source
    .filter((fixture, index, list) => list.findIndex((item) => item.id === fixture.id) === index)
    .sort((a, b) => fixtureSortValue(a).localeCompare(fixtureSortValue(b)))
    .map((fixture) => ({ fixture, match: matchesById.get(fixture.id) || null }));
});

const groupedFixtures = computed(() => {
  const groups = new Map();
  for (const item of fixtureItems.value) {
    const key = fixtureDateKey(item.fixture.date) || item.fixture.stage || 'tbd';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return [...groups.entries()].map(([key, items]) => ({ key, label: shortDate(items[0]?.fixture?.date || key), items }));
});

watch(isRTL, (value) => {
  document.documentElement.dir = value ? 'rtl' : 'ltr';
}, { immediate: true });

watch(() => simulationResults.value.length, async () => {
  await nextTick();
  if (simulationLogEl.value) simulationLogEl.value.scrollTop = simulationLogEl.value.scrollHeight;
});

onMounted(async () => {
  try {
    manifest.value = await loadWorldCupData();
    fetchRankings();
  } catch (error) {
    errorMessage.value = error?.message || t('common.requestFailed');
  } finally {
    loading.value = false;
  }
});

async function startSimulation() {
  if (!manifest.value || processing.value) return;
  processing.value = true;
  errorMessage.value = '';
  simulationResults.value = [];
  simulationOverlayOpen.value = true;
  championRecorded.value = false;
  trackAnalytics('simple_sim_start');
  try {
    const team = strongestTeam(manifest.value.teams);
    const next = createInitialRun(manifest.value, {
      selectedTeamId: team.id,
      seed: newRunSeed(),
      startingXi: team.defaultStartingXi,
    });
    while (!next.completed && next.nextMatch) {
      await sleep(randomSimulationDelay());
      stepRun(manifest.value, next);
      const match = next.matches[next.matches.length - 1];
      if (match) {
        simulationResults.value.push(simulationResultRow(match));
        run.value = { ...next, matches: [...next.matches] };
      }
    }
    run.value = { ...next };
    await recordChampion(next);
    simulationOverlayOpen.value = false;
    trackAnalytics('simple_sim_complete', { matches: next.matches.length });
  } catch (error) {
    errorMessage.value = error?.message || t('common.requestFailed');
  } finally {
    processing.value = false;
  }
}

function teamFlag(teamId) {
  return flag(teamId || 'tbd');
}

function teamName(teamId) {
  return translatedTeamName(teamId || 'tbd');
}

function teamById(teamId) {
  return manifest.value?.teams?.find((team) => team.id === teamId) || null;
}

function stageLabel(fixture) {
  if (fixture.group) return t('landing.groupLabel', { id: fixture.group });
  return stageName(fixture.stage);
}

function scoreHtml(item) {
  if (!item.match) return `<span class="score-vs">${t('common.vs')}</span>`;
  return finalScoreHtml(item.match);
}

function goalEvents(item, teamId) {
  if (!item.match) return [];
  return matchTeamGoalEvents(item.match, teamId);
}

function randomSimulationDelay() {
  return 50 + Math.floor(Math.random() * 51);
}

function simulationResultRow(match) {
  return {
    id: match.id,
    date: shortDate(match.date),
    stage: stageName(match.stage),
    home: teamName(match.homeTeamId),
    away: teamName(match.awayTeamId),
    score: finalScore(match),
  };
}

async function fetchRankings() {
  rankingsLoading.value = true;
  try {
    const response = await fetch('/api/rankings.json');
    if (!response.ok) throw new Error(t('common.requestFailed'));
    rankingsData.value = await response.json();
  } catch {
    rankingsData.value = { coach: [], champion: [] };
  } finally {
    rankingsLoading.value = false;
  }
}

async function recordChampion(nextRun) {
  if (championRecorded.value || !nextRun?.completed) return;
  const final = nextRun.matches.find((match) => match.stage === 'Final') || nextRun.matches[nextRun.matches.length - 1];
  const championId = final?.winnerTeamId || final?.penaltyWinnerTeamId;
  if (!championId) return;
  try {
    const response = await fetch('/api/record', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'champion', teamId: championId }),
    });
    if (!response.ok) throw new Error(t('common.requestFailed'));
    championRecorded.value = true;
    upsertLocalChampionRanking(championId);
  } catch (error) {
    console.warn('[simple] failed to record champion ranking', error);
  }
}

function openChampionRanking() {
  championRankingOpen.value = true;
  if (!rankingsData.value && !rankingsLoading.value) fetchRankings();
  trackAnalytics('simple_champion_ranking_open');
}

function upsertLocalChampionRanking(teamId) {
  const current = rankingsData.value || { coach: [], champion: [] };
  const champion = Array.isArray(current.champion) ? [...current.champion] : [];
  const existing = champion.find((item) => item.teamId === teamId);
  if (existing) existing.count = Number(existing.count || 0) + 1;
  else champion.push({ teamId, count: 1 });
  champion.sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
  rankingsData.value = { ...current, champion: champion.slice(0, 20) };
}

function trackAnalytics(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}
</script>
